import { Injectable } from '@angular/core';
import { ActivityInfo } from '@digipay/ngx-payment-result/lib/model/payment-result.model';
import { SeparateThousandsPipe } from '@digipay/ng-lib-pipes';
import { IReceipt } from '../../../data-access/models/receipt.interface';
import { TransactionTypeEnum } from '../../../data-access/enums/transaction-type.enum';
import { OrderStatus } from '../../../data-access/enums/order-status';

@Injectable({
  providedIn: 'root',
})
export class ReceiptHandlerService {
  private receipt: IReceipt;
  private separateThousandsPipe = new SeparateThousandsPipe();

  constructor() {}

  set _setReceiptData(data: IReceipt) {
    this.receipt = data;
  }

  public _generateWaitingMessage(): string {
    return this.displayMessage(this.receipt)
      ? this.generateMessage(this.receipt.transactionType, this.receipt.investmentType, this.receipt.status, this.receipt.instrumentType)
      : '';
  }

  public _getReceiptInfo(): {
    title: string;
    result: 'error' | 'success' | 'unKnown' | 'waiting';
  } {
    const { transactionType, status } = this.receipt;

    const isBuyTransaction = transactionType === TransactionTypeEnum.Buy;
    const isWalletWhitedeawalTransaction = transactionType === TransactionTypeEnum.EtfWalletWithdrawal;
    const isWalletDepositTransaction = transactionType === TransactionTypeEnum.EtfWalletDeposit;

    // Handle special cases for "Profit" and "Overplus" upfront
    if (transactionType === TransactionTypeEnum.Profit || transactionType === TransactionTypeEnum.Overplus) {
      const titleKey = transactionType === TransactionTypeEnum.Profit ? 'سود سرمایه‌گذاری واریز شد' : 'تتمه سرمایه‌گذاری واریز شد';
      return { title: titleKey, result: 'success' };
    }

    // Map statuses to their respective results and translation keys
    const statusMappings: Record<OrderStatus, { result: 'error' | 'success' | 'waiting'; titleKey: string }> = {
      [OrderStatus.Draft]: {
        result: 'waiting',
        titleKey:
          this.receipt.instrumentType === 'IPO'
            ? 'درخواست خرید شما ثبت شد'
            : isBuyTransaction
              ? 'سرمایه‌گذاری در حال انجام است'
              : isWalletWhitedeawalTransaction
                ? 'برداشت از کیف پول در حال انجام است'
                : isWalletDepositTransaction
                  ? ''
                  : 'فروش در حال انجام است',
      },
      [OrderStatus.Waiting]: {
        result: 'waiting',
        titleKey: isBuyTransaction
          ? 'سرمایه‌گذاری در حال انجام است'
          : isWalletWhitedeawalTransaction
            ? 'برداشت از کیف پول در حال انجام است'
            : isWalletDepositTransaction
              ? ''
              : 'فروش در حال انجام است',
      },
      [OrderStatus.RejectedByManager]: {
        result: 'error',
        titleKey:
          this.receipt.instrumentType === 'IPO'
            ? 'درخواست خرید شما انجام نشد'
            : isBuyTransaction
              ? 'درخواست خرید شما توسط مدیر صندوق رد شد'
              : 'درخواست فروش شما توسط مدیر صندوق رد شد',
      },
      [OrderStatus.RejectedBySystem]: {
        result: 'error',
        titleKey:
          this.receipt.instrumentType === 'IPO'
            ? 'درخواست خرید شما انجام نشد'
            : isWalletWhitedeawalTransaction
              ? 'درخواست برداشت شما توسط سیستم رد شد'
              : isBuyTransaction
                ? 'درخواست خرید شما توسط سیستم رد شد'
                : 'درخواست فروش شما توسط سیستم رد شد',
      },
      [OrderStatus.Approved]: {
        result: 'success',
        titleKey:
          this.receipt.instrumentType === 'IPO'
            ? 'خرید شما انجام شد'
            : isWalletWhitedeawalTransaction
              ? 'درخواست برداشت از کیف پول با موفقیت ثبت شد. '
              : isWalletDepositTransaction
                ? 'واریز انجام شد'
                : isBuyTransaction
                  ? 'سرمایه‌گذاری انجام شد'
                  : 'فروش انجام شد',
      },
      [OrderStatus.Deleted]: {
        result: 'error',
        titleKey: isBuyTransaction
          ? this.receipt.instrumentType === 'IPO'
            ? 'درخواست خرید شما حذف شد'
            : 'درخواست سرمایه‌گذاری حذف شد'
          : 'درخواست فروش حذف شد',
      },
      [OrderStatus.RemoteOrderStatusEnum]: {
        result: 'error', // Provide a default or specific result
        titleKey: 'درخواست نامشخص', // A placeholder key for now
      },
    };

    // Handle statuses that exist in the mapping
    if (status in statusMappings) {
      const { titleKey, result } = statusMappings[status];
      return { title: titleKey, result };
    }

    // Default fallback for unhandled cases
    return { title: '', result: 'unKnown' };
  }

  public _setFields(): ActivityInfo[] {
    if (this.receipt.instrumentType === 'Wallet') {
      return [
        this.createField('زمان ثبت درخواست', this.getFormattedTime()),
        this.createField(this.getReceiptNumberText(), this.receipt.receiptNumber, true),
      ];
    }

    if (this.receipt.instrumentType === 'IPO') {
      return [
        this.createField('نوع سرمایه‌گذاری', `عرضه اولیه ${this.receipt.instrumentName}`),
        this.receipt.units > 0
          ? this.createField('تعداد سهم', `${this.receipt.units} ` + 'سهم')
          : this.createField('تعداد سهم', 'در حال بررسی'),
        this.createField('زمان ثبت درخواست', this.getFormattedTime()),
        this.createField(this.getTimeText(), this.getFormattedConfirmationTime()),
        this.createField('نوع پرداخت', this.getIPOPaymentMethod()),
        this.createField('محل تراکنش', this.getTransactionArea()),
        this.createField(this.getReceiptNumberText(), this.receipt.receiptNumber, true),
      ];
    }

    if (this.isCrowdTransaction()) {
      return [
        this.createField('نوع سرمایه‌گذاری', 'تامین مالی جمعی'),
        this.createField('نام طرح', this.receipt.instrumentName),
        this.createField('زمان', this.getFormattedTime()),
        this.createField('محل تراکنش', this.getTransactionArea()),
        this.createField('کد رهگیری', this.receipt.receiptNumber, true),
      ];
    }

    const commonFields = this.getCommonFields();

    if (this.isWalletWithdrawTransaction()) {
      return [
        // ...commonFields,
        this.createField('برداشت از', 'کیف پول صندوق‌های ETF'),
        this.createField('زمان', this.getFormattedTime()),
        this.createField('کد رهگیری', this.receipt.receiptNumber, true),
      ];
    }

    if (this.isWalletDepositTransaction()) {
      return [
        this.createField('واریز به', 'کیف پول صندوق‌های ETF'),
        this.createField('زمان', this.getFormattedTime()),
        this.createField('کد رهگیری', this.receipt.receiptNumber, true),
      ];
    }

    if (this.isProfitOrOverplusTransaction()) {
      return [...commonFields, ...this.getETFData()];
    }

    if (this.isDraftOrWaitingOrRejectedStatus()) {
      return [
        ...commonFields,
        ...this.getETFData(),
        this.createField('محل تراکنش', this.getTransactionArea()),
        this.createField('کد رهگیری', this.receipt.receiptNumber, true),
      ];
    }

    return [
      ...commonFields,
      ...this.getETFData(),
      ...this.units(),
      this.createField('محل تراکنش', this.getTransactionArea()),
      this.createField('کد رهگیری', this.receipt.receiptNumber, true),
    ];
  }

  private units(): ActivityInfo[] {
    if (this.receipt?.investmentType != 'ETF') {
      return [this.createField('تعداد واحد', this.receipt?.units?.toString() || '')];
    } else {
      if (this.receipt.units) {
        return [this.createField('تعداد واحد', this.receipt?.units?.toString() || '')];
      }
    }
    return [];
  }

  private displayMessage(info: IReceipt): boolean {
    return (
      ((info.status === OrderStatus.Draft || info.status === OrderStatus.Waiting) &&
        info.transactionType !== TransactionTypeEnum.Overplus &&
        info.transactionType !== TransactionTypeEnum.Profit) ||
      (info?.transactionType === 'Sell' && info?.investmentType === 'ETF')
    );
  }

  private generateMessage(
    transactionType: TransactionTypeEnum,
    investmentType: string,
    status: OrderStatus,
    instrumentType?: string,
  ): string {
    let text = '';
    if (transactionType == TransactionTypeEnum.Buy) {
      if (instrumentType === 'IPO' && status === 'Draft') {
        text = `سرمایه‌گذاری شما در تاریخ ${this.receipt.ipoDate.split(' ')[0]} به صورت خودکار انجام خواهد شد.`;
      } else {
        text = 'نهایی شدن سرمایه‌گذاری شما  ۱ تا ۲ روز کاری زمان می‌برد';
      }
    } else if (transactionType == TransactionTypeEnum.Sell && investmentType === 'ETF') {
      if (status === 'Approved') {
        text = 'مبلغ حاصل از فروش به کیف پول صندوق های ETF شما واریز شده است.';
      } else if (status === 'Draft' || status === 'Waiting') {
        text = 'می توانید مبلغ حاصل از فروش را از کیف پول خود برداشت کنید.';
      }
    } else {
      if (transactionType == TransactionTypeEnum.EtfWalletWithdrawal) {
        text = 'نهایی شدن برداشت شما حداکثر 2 روز کاری زمان می‌برد';
      } else {
        text = 'نهایی شدن فروش شما  ۱ تا ۲ روز کاری زمان می‌برد';
      }
    }
    return text;
  }

  private getCommonFields(): ActivityInfo[] {
    return [
      this.createField('نام صندوق', this.receipt.instrumentName),
      this.createField(
        'نوع صندوق',
        this.receipt.instrumentType === 'FixedIncome' ? 'درامد ثابت' : this.receipt.instrumentType === 'Gold' ? 'مبتنی بر طلا' : 'سهامی',
      ),
      this.createField('زمان', this.getFormattedTime()),
    ];
  }

  private getFormattedTime(): string {
    return `${this.receipt.date}${this.receipt.time ? ' - ' + this.receipt.time : ''}`;
  }

  private getTimeText(): string {
    switch (this.receipt.status) {
      case OrderStatus.Deleted:
      case OrderStatus.RejectedByManager:
      case OrderStatus.RejectedBySystem:
        return 'زمان حذف درخواست';
      case OrderStatus.Approved:
        return 'زمان خرید';
      default:
        return '';
    }
  }
  private getFormattedConfirmationTime(): string {
    return `${this.receipt?.confirmationDate}${this.receipt?.confirmationTime ? ' - ' + this.receipt?.confirmationTime : ''}`;
  }

  private isProfitOrOverplusTransaction(): boolean {
    return this.receipt.transactionType === TransactionTypeEnum.Profit || this.receipt.transactionType === TransactionTypeEnum.Overplus;
  }

  private isWalletWithdrawTransaction(): boolean {
    return this.receipt.transactionType === TransactionTypeEnum.EtfWalletWithdrawal;
  }

  private isWalletDepositTransaction(): boolean {
    return this.receipt.transactionType === TransactionTypeEnum.EtfWalletDeposit;
  }

  private isDraftOrWaitingOrRejectedStatus(): boolean {
    return [OrderStatus.Draft, OrderStatus.Waiting, OrderStatus.RejectedByManager, OrderStatus.RejectedBySystem].includes(
      this.receipt.status,
    );
  }

  private getTransactionArea(): string {
    return this.receipt.isTradePlaceInternal ? 'دیجی‌پی' : 'خارج از دیجی‌پی';
  }
  private getIPOPaymentMethod(): string {
    return this.receipt.ipoPaymentMethod === 'ByCredit' ? 'اعتبار کارگزاری' : 'درگاه بانکی';
  }
  private getReceiptNumberText(): string {
    return this.receipt.status === 'Draft' || this.receipt.status === 'Waiting' || this.receipt.status === 'Deleted'
      ? 'شماره درخواست'
      : 'کد رهگیری';
  }

  private getETFData(): ActivityInfo[] {
    if (this.receipt.paidAmountBreakdown) {
      const result: ActivityInfo[] = [];
      if (this.receipt.paidAmountBreakdown.ipg > 0) {
        result.push(this.createField('پرداخت مستقیم', this.priceValue(this.receipt.paidAmountBreakdown.ipg)));
      }
      if (this.receipt.paidAmountBreakdown.wallet > 0) {
        result.push(this.createField('برداشت از کیف پول ETF', this.priceValue(this.receipt.paidAmountBreakdown.wallet)));
      }
      return result;
    }
    return [];
  }

  private createField(key: string, value: string, copyable = false): ActivityInfo {
    if (!key) return { key: '', value: '', copyable: false };
    return {
      key,
      value,
      copyable,
    };
  }

  private priceValue(price: number): string {
    return this.separateThousandsPipe.transform(price) + ' ریال';
  }

  private isCrowdTransaction() {
    return this.receipt.investmentType === 'CrowdFund';
  }
}
